import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus, Trash2, Edit3, RefreshCw, X, Loader2, Search } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { PageHeader, AdminCard, Loading, Empty, Btn, Input, Select, Textarea } from './shared';
import { useToast } from '../../hooks/use-toast';

const TYPES = ['all', 'did', 'tollfree', 'uifn'];
const BLANK = {
  country_code: '', country_name: '', type: 'did',
  kyc: 'Not required', documents: '', reachability: '', notes: '', processing_time: '',
};
const KYC_OPTS = ['Not required', 'Worldwide', 'In country', 'In region'];

export default function AdminRegulations() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setItems((await api.get('/api/admin/regulations')).data || []); }
    catch (err) { toast({ title: 'Failed', description: formatApiError(err) }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!editing.country_name?.trim()) return toast({ title: 'Country name required' });
    setSaving(true);
    try {
      const body = {
        country_code: (editing.country_code || '').toUpperCase(),
        country_name: editing.country_name,
        type: editing.type || 'did',
        kyc: editing.kyc || '',
        documents: (typeof editing.documents === 'string' ? editing.documents.split('\n') : (editing.documents || [])).map((d) => d.trim()).filter(Boolean),
        reachability: editing.reachability || '',
        summary: editing.summary || editing.kyc || '',
        notes: editing.notes || '',
        processing_time: editing.processing_time || '',
      };
      if (editing.id) await api.patch(`/api/admin/regulations/${editing.id}`, body);
      else await api.post('/api/admin/regulations', body);
      toast({ title: 'Saved' });
      setEditing(null); load();
    } catch (err) { toast({ title: 'Save failed', description: formatApiError(err) }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this regulation?')) return;
    try { await api.delete(`/api/admin/regulations/${id}`); load(); toast({ title: 'Deleted' }); }
    catch (err) { toast({ title: 'Delete failed', description: formatApiError(err) }); }
  };

  const startEdit = (r) => setEditing({
    ...r,
    documents: (r.documents || []).join('\n'),
  });

  const filtered = useMemo(() => items.filter((r) => {
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (!query) return true;
    const q = query.toLowerCase();
    return [r.country_name, r.country_code, r.kyc, r.reachability].some((v) => (v || '').toLowerCase().includes(q));
  }), [items, typeFilter, query]);

  return (
    <>
      <PageHeader
        title="KYC Regulations"
        subtitle="Country + number-type specific KYC document requirements"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={load}><RefreshCw size={14} /> Refresh</Btn>
            <Btn onClick={() => setEditing({ ...BLANK })}><Plus size={14} /> Add row</Btn>
          </div>
        }
      />

      <AdminCard>
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="flex gap-2">
            {TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setTypeFilter(t)}
                className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border ${
                  typeFilter === t
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white border-transparent'
                    : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'
                }`}
              >{t}</button>
            ))}
          </div>
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search country, KYC, reachability…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800/80 border border-white/10 focus:border-fuchsia-400 focus:ring-2 focus:ring-fuchsia-500/30 outline-none text-white text-sm placeholder:text-slate-500"
            />
          </div>
          <div className="text-xs text-slate-400 self-center">Showing {filtered.length} of {items.length}</div>
        </div>

        {loading ? <Loading /> : filtered.length === 0 ? <Empty /> : (
          <div className="overflow-x-auto -mx-5 md:-mx-6 px-5 md:px-6 max-h-[70vh]">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-900/95 backdrop-blur z-10">
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2">Country</th>
                  <th className="py-2">Type</th>
                  <th className="py-2">KYC</th>
                  <th className="py-2 hidden md:table-cell">Documents</th>
                  <th className="py-2 hidden lg:table-cell">Reachability</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((r) => (
                  <tr key={r.id} className="hover:bg-white/5 align-top">
                    <td className="py-2 font-semibold text-white">{r.country_name}</td>
                    <td className="py-2 text-xs uppercase text-violet-300">{r.type}</td>
                    <td className="py-2 text-slate-300 text-xs">{r.kyc}</td>
                    <td className="py-2 text-slate-400 hidden md:table-cell text-xs">{(r.documents || []).slice(0, 2).join(' · ')}{(r.documents || []).length > 2 && '…'}</td>
                    <td className="py-2 text-slate-400 hidden lg:table-cell text-xs line-clamp-2 max-w-xs">{r.reachability}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex gap-1">
                        <Btn size="sm" variant="ghost" onClick={() => startEdit(r)}><Edit3 size={12} /></Btn>
                        <Btn size="sm" variant="danger" onClick={() => remove(r.id)}><Trash2 size={12} /></Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AdminCard>

      {editing && (
        <div className="fixed inset-0 z-50 flex" onClick={() => !saving && setEditing(null)}>
          <div className="flex-1 bg-black/60" />
          <aside className="w-full max-w-lg bg-slate-900 border-l border-white/10 p-6 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold text-white">{editing.id ? 'Edit regulation' : 'New regulation'}</h3>
              <button onClick={() => setEditing(null)} className="p-2 rounded-lg hover:bg-white/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Input label="Country name *" value={editing.country_name} onChange={(e) => setEditing({ ...editing, country_name: e.target.value })} />
                <Input label="ISO-2 code" value={editing.country_code} onChange={(e) => setEditing({ ...editing, country_code: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Select label="Number type" value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}>
                  {['did','tollfree','uifn'].map((t) => <option key={t} value={t}>{t.toUpperCase()}</option>)}
                </Select>
                <Select label="KYC" value={editing.kyc || 'Not required'} onChange={(e) => setEditing({ ...editing, kyc: e.target.value })}>
                  {KYC_OPTS.map((k) => <option key={k} value={k}>{k}</option>)}
                </Select>
              </div>
              <Textarea label="Required documents (one per line)" rows={5} value={editing.documents} onChange={(e) => setEditing({ ...editing, documents: e.target.value })} placeholder={'Certificate of Incorporation\nProof of Address\nDirector ID'} />
              <Textarea label="Reachability" rows={2} value={editing.reachability} onChange={(e) => setEditing({ ...editing, reachability: e.target.value })} placeholder="National: full · International: yes · Max 180 min" />
              <Input label="Processing time" value={editing.processing_time} onChange={(e) => setEditing({ ...editing, processing_time: e.target.value })} placeholder="5-10 business days" />
              <Textarea label="Notes" rows={2} value={editing.notes} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </div>
            <div className="mt-6 pt-4 border-t border-white/10 flex gap-2 justify-end">
              <Btn variant="ghost" onClick={() => setEditing(null)} disabled={saving}>Cancel</Btn>
              <Btn onClick={save} disabled={saving}>{saving ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : 'Save'}</Btn>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}
